import { useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import { Metrics } from 'collaboration-sidebar/utils/metrics';
import ErrorBoundary from './ErrorBoundary';
import { HAS_UNRECOVERABLE_ERROR } from '../../constants/CollaborationSidebarStateProperties';
import debounce from '../../utils/debounce';
import { jsx as _jsx } from "react/jsx-runtime";
const TIME_TO_RESET = 3000;
function useRenderCount() {
  const renderCount = useRef(0);
  const debouncedReset = useMemo(() => debounce(() => {
    renderCount.current = 0;
  }, TIME_TO_RESET), []);
  useEffect(() => {
    renderCount.current += 1;
    debouncedReset();
  });
  return renderCount.current;
}

/**
 * From: https://git.hubteam.com/HubSpot/Critsit/issues/4039
 * This is a somehow hacky approach but could be useful for us to remediate errors when ErrorBoundary is not enough.
 *
 * This component will render its children until a certain threshold and then it'll stop rendering and notify us in Grafana.
 */
export default function ErrorCountWrapper({
  appName,
  children,
  errorBoundaryFingerprint,
  threshold,
  onUpdateCollaborationSidebarState
}) {
  const renderCount = useRenderCount();
  const hasRenderedTooMuch = renderCount > threshold;
  useEffect(() => {
    if (!hasRenderedTooMuch) {
      return;
    }
    onUpdateCollaborationSidebarState({
      [HAS_UNRECOVERABLE_ERROR]: true
    });
    Metrics.counter('infinite-loop', {
      app_name: appName
    }).increment();
    Raven.captureException(new Error(`[${appName}] ${errorBoundaryFingerprint} rendered too many times`), {
      extra: {
        appName
      },
      fingerprint: [errorBoundaryFingerprint],
      tags: {
        lib: 'collaboration-sidebar'
      }
    });
  }, [appName, errorBoundaryFingerprint, hasRenderedTooMuch, onUpdateCollaborationSidebarState]);
  if (hasRenderedTooMuch) {
    return null;
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fingerprint: errorBoundaryFingerprint,
    onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
    renderFallback: () => null,
    children: children
  });
}